import { useEffect, useState } from 'react';
import { Button, Modal, Form, Toast, ToastContainer } from 'react-bootstrap';
import { Display, Pencil, Plus, Trash } from 'react-bootstrap-icons';
import DataTable from 'react-data-table-component';
import Select from 'react-select';
import api from '../api';

const Usuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [filteredUsuarios, setFilteredUsuarios] = useState([]);
  const [search, setSearch] = useState('');
  const [salas, setSalas] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [selectedUsuario, setSelectedUsuario] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  const [isSaving, setIsSaving] = useState(false);
  const [usuarioAEliminar, setUsuarioAEliminar] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const user = JSON.parse(localStorage.getItem('user'));

  const normalizeText = (text) => {
    return text
      ?.toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, ""); // quita acentos
  };

  useEffect(() => {
    api.get('/usuarios').then(res => {
      setUsuarios(res.data)
      setFilteredUsuarios(res.data)
    });
    if (user?.role === 'admin') {
      api.get('/empresas').then(res => setEmpresas(res.data));
    }
  }, []);

   useEffect(() => {
    const result = usuarios.filter(item =>
      normalizeText(item.name)?.includes(normalizeText(search)) ||
      normalizeText(item.email)?.includes(normalizeText(search)) ||
      normalizeText(item.role)?.includes(normalizeText(search)) ||
      normalizeText(item.salas?.[0]?.empresa?.nombre)?.includes(normalizeText(search)) 
    );
    setFilteredUsuarios(result);
  }, [search, usuarios]);

  useEffect(() => {
    const cargarSalas = async () => {
      if (!selectedUsuario?.id && (selectedUsuario?.role === 'coordinador' || selectedUsuario?.role === 'consulta')) {
        if (user?.role === 'admin' && selectedUsuario?.empresa_id) {
          const res = await api.get(`/salas-por-empresa/${selectedUsuario?.empresa_id}`);
          setSalas(res.data);
        }

        if (user?.role === 'coordinador') {
          const res = await api.get('/salas-por-usuario');
          setSalas(res.data);
        }
      }
    };

    cargarSalas();
  }, [selectedUsuario?.role, selectedUsuario?.empresa_id]);

  useEffect(() => {
    if (selectedUsuario?.role === 'admin') {
      // Limpiar empresa y salas si cambia a admin
      setSelectedUsuario(prev => ({
        ...prev,
        empresa_id: null,
        salas: []
      }));
      setSalas([]); // También limpia el listado de salas cargadas
    }
  }, [selectedUsuario?.role]);

  const handleEmpresaChange = async (selected) => {
    const idEmpresa = selected.value;
    setSelectedUsuario(prev => ({
      ...prev,
      empresa_id: idEmpresa,
      salas: [], // Reinicia salas al cambiar empresa
    }));
    const response = await api.get(`/salas-por-empresa/${idEmpresa}`);
    setSalas(response.data);
  };

  const handleShowModal = async (usuario = null) => {
    setSalas([]);
    if (usuario) {
      const estadoBase = {
        id: usuario.id,
        name: usuario.name,
        email: usuario.email,
        password: '',
        password_confirmation: '',
        role: usuario?.role,
        salas: usuario.salas.map(s => s.id),
      };

      if (usuario?.role !== 'admin' && usuario.salas.length > 0) {
        const empresaId = usuario.salas[0].empresa_id;
        estadoBase.empresa_id = empresaId;

        // a. Si quien edita es admin → cargar salas por empresa
        if (user?.role === 'admin') {
          const res = await api.get(`/salas-por-empresa/${empresaId}`);
          setSalas(res.data);
        }

        // b. Si quien edita es coordinador → cargar sus propias salas
        if (user?.role === 'coordinador') {
          const res = await api.get(`/salas-por-usuario`);
          setSalas(res.data);
        }
      }

      setSelectedUsuario(estadoBase);

    } else {
      setSelectedUsuario({
        name: '',
        email: '',
        password: '',
        password_confirmation: '',
        role: '',
        empresa_id: null,
        salas: []
      });

      // const res = user.role !== 'admin' && await api.get('/salas-por-usuario');
      // if (res.data && user.role !== 'admin') setSalas(res.data);

    }

    setErrors({});
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedUsuario(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (idSala) => {
    setSelectedUsuario(prev => {
      const salas = prev.salas.includes(idSala)
        ? prev.salas.filter(id => id !== idSala)
        : [...prev.salas, idSala];
      return { ...prev, salas };
    });
  };

  const validate = () => {
    const newErrors = {};
    if (!selectedUsuario.name?.trim()) {
      newErrors.name = "Campo requerido";
    } 

    if (!selectedUsuario.email?.trim()) {
      newErrors.email = "Campo requerido";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(selectedUsuario.email)) {
      newErrors.email = "Correo electrónico inválido";
    }

    if (!selectedUsuario.role?.trim()) {
      newErrors.role = "Debe seleccionar un rol";
    }

    // Validaciones de contraseña (solo si es nuevo usuario)
    if (!selectedUsuario.id) {
      if (!selectedUsuario.password) {
        newErrors.password = "Campo requerido";
      }

      if (!selectedUsuario.password_confirmation) {
        newErrors.password_confirmation = "Campo requerido";
      }

      if (
        selectedUsuario.password &&
        selectedUsuario.password_confirmation &&
        selectedUsuario.password !== selectedUsuario.password_confirmation
      ) {
        newErrors.password_confirmation = "Las contraseñas no coinciden";
      }

    }

    const isAdmin = selectedUsuario.role === 'admin';

    // Validar empresa solo si el nuevo usuario NO es admin
    if (!isAdmin && !selectedUsuario.empresa_id) {
      newErrors.empresa_id = "Debe seleccionar una empresa";
    }

    // Validar salas solo si el nuevo usuario NO es admin
    if (!isAdmin && (!selectedUsuario.salas || selectedUsuario.salas.length === 0)) {
      newErrors.salas = "Debe seleccionar al menos una sala";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const isEdit = !!selectedUsuario.id;
    setIsSaving(true);
    try {
      const payload = { ...selectedUsuario };
      if (!isEdit) {
        const res = await api.post('/usuarios', payload);
        setUsuarios([...usuarios, res.data.data]);
        setFilteredUsuarios([...usuarios, res.data.data]);
        showToast('success', 'Usuario creado correctamente');
      } else {
        const res = await api.put(`/usuarios/${selectedUsuario.id}`, payload);
        setUsuarios(usuarios.map(u => u.id === selectedUsuario.id ? res.data.data : u));
        setFilteredUsuarios(usuarios.map(u => u.id === selectedUsuario.id ? res.data.data : u));
        showToast('success', 'Usuario actualizado correctamente');
      }
      setShowModal(false);
    } catch (err) {
      if (err.response?.data?.errors) setErrors(err.response.data.errors);
      showToast('danger', 'Error al guardar');
    } finally {
      setIsSaving(false);
    }
  };

  const showToast = (variant, message) => {
    setToast({ show: true, variant, message });
    setTimeout(() => setToast({ show: false, variant, message: '' }), 3000);
  };

  const handleDelete = (row) => {
    setUsuarioAEliminar(row);
    setShowDeleteModal(true);
  };

  const confirmarEliminacion = () => {
    setIsDeleting(true);
    api.delete(`/usuarios/${usuarioAEliminar.id}`)
      .then(() => {
        const listaNueva = usuarios.filter(user => user.id !== usuarioAEliminar.id);
        setUsuarios(listaNueva);
        showToast('success', 'Usuario eliminado correctamente.');
      })
      .catch(() => showToast('danger', 'Error al eliminar el usuario.'))
      .finally(() => {
        setIsDeleting(false);
      });
    setShowDeleteModal(false);
  };

  const columns = [
    { name: 'Nombre', selector: row => row.name },
    { name: 'Correo', selector: row => row.email },
    { name: 'Rol', selector: row => row.role },
    { name: 'Empresa', selector: row => row.salas?.[0]?.empresa?.nombre || '-' },
    {
      name: 'Opciones',
      cell: row => (
        <div className="d-flex gap-2">
          <Button variant="outline-primary" size="sm" onClick={() => handleShowModal(row)}>
            <Pencil />
          </Button>
          <Button
            variant="outline-danger"
            size="sm"
            onClick={() => handleDelete(row)}
          >
            <Trash />
          </Button>
        </div>
      ),
      ignoreRowClick: true,
    },
  ];

  const isEditing = !!selectedUsuario?.id;
  const mostrarEmpresaYSalas =
    selectedUsuario?.role === 'coordinador' || selectedUsuario?.role === 'consulta';

    if (!user?.role) {
  return <div className="p-4">Cargando usuario...</div>;
}


  return (
    <div className="mt-4">
      <DataTable
        title="Listado de Usuarios"
        columns={columns}
        data={filteredUsuarios}
        pagination
        highlightOnHover
        responsive
        striped
        subHeader
        subHeaderComponent={
          user?.role !== 'consulta' ? (
            <div className="d-flex justify-content-between w-100">
              <Button variant="outline-success" size="sm" onClick={() => handleShowModal()}>Nuevo <Plus /></Button>
              <input
                type="text"
                className="form-control w-25"
                placeholder="Buscar..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          ) : (
            <div className="d-flex justify-content-between w-100">
              <input
                type="text"
                className="form-control w-25"
                placeholder="Buscar..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          )    
        }
      />

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedUsuario?.id ? 'Editar Usuario' : 'Nuevo Usuario'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nombre</Form.Label>
              <Form.Control
                name="name"
                value={selectedUsuario?.name || ''}
                onChange={handleInputChange}
                isInvalid={!!errors.name}
              />
              <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Correo</Form.Label>
              <Form.Control
                name="email"
                type="email"
                value={selectedUsuario?.email || ''}
                onChange={handleInputChange}
                isInvalid={!!errors.email}
              />
              <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
            </Form.Group>
            
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Contraseña</Form.Label>
                  <Form.Control
                    name="password"
                    type="password"
                    onChange={handleInputChange}
                    isInvalid={!!errors.password}
                  />
                  <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Confirmar contraseña</Form.Label>
                  <Form.Control
                    name="password_confirmation"
                    type="password"
                    onChange={handleInputChange}
                    isInvalid={!!errors.password_confirmation}
                  />
                  <Form.Control.Feedback type="invalid">{errors.password_confirmation}</Form.Control.Feedback>
                </Form.Group>
              </>
            <Form.Group className="mb-3">
              <Form.Label>Rol</Form.Label>
              <div className={errors.role ? 'is-invalid' : ''}>
                <Select
                  name="role"
                  options={[
                    { value: 'admin', label: 'Admin', isDisabled: user?.role !== 'admin' },
                    { value: 'coordinador', label: 'Coordinador' },
                    { value: 'consulta', label: 'Consulta' }
                  ]}
                  value={[
                    { value: 'admin', label: 'Admin', isDisabled: user?.role !== 'admin' },
                    { value: 'coordinador', label: 'Coordinador' },
                    { value: 'consulta', label: 'Consulta' }
                  ].find(opt => opt.value === selectedUsuario?.role) || null}
                  onChange={selected => setSelectedUsuario({ ...selectedUsuario, role: selected.value })}
                />
              </div>
              {errors.role && (
                <div className="invalid-feedback d-block">{errors.role}</div>
              )}
            </Form.Group>
            {/* Mostrar empresa solo si quien crea es admin, se está creando un usuario nuevo, y el nuevo usuario NO es admin */}
            {user?.role === 'admin' && !isEditing && selectedUsuario?.role !== 'admin' && (
              <Form.Group className="mb-3">
                <Form.Label>Empresa</Form.Label>
                <div className={errors.empresa_id ? 'is-invalid' : ''}>
                  <Select
                    name="empresa_id"
                    options={empresas.map(emp => ({
                      value: emp.id,
                      label: emp.nombre
                    }))}
                    value={
                      empresas
                        .map(emp => ({ value: emp.id, label: emp.nombre }))
                        .find(opt => opt.value === selectedUsuario?.empresa_id) || null
                    }
                    onChange={handleEmpresaChange}
                  />
                </div>
                {errors.empresa_id && (
                  <div className="invalid-feedback d-block">{errors.empresa_id}</div>
                )}
              </Form.Group>
            )}

            {/* Mostrar salas solo si el rol seleccionado es coordinador o consulta */}
            {mostrarEmpresaYSalas && (
              <Form.Group className="mb-3">
                <Form.Label>Salas</Form.Label>
                {salas.map(sala => (
                  <Form.Check
                    key={sala.id}
                    type="checkbox"
                    label={sala.nombre_sala}
                    checked={selectedUsuario?.salas?.includes(sala.id) || false}
                    onChange={() => handleCheckboxChange(sala.id)}
                  />
                ))}
                {!!errors.salas && (
                  <div className="text-danger small">Debe seleccionar al menos una sala</div>
                )}
              </Form.Group>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
          <Button variant="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Guardando...' : 'Guardar'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Confirmación para eliminar */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar eliminación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Está seguro de que desea eliminar el usuario <strong>{usuarioAEliminar?.name}</strong>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancelar
          </Button>
          <Button variant="danger"
            onClick={confirmarEliminacion}
            disabled={isDeleting}
          >
            {isDeleting ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer position="top-end" className="p-3">
        <Toast
          bg={toast.variant}
          onClose={() => setToast({ ...toast, show: false })}
          show={toast.show}
          delay={3000}
          autohide
        >
          <Toast.Body className="text-white">{toast.message}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default Usuarios;
